import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { get } from 'api'
import { usePerson } from 'contexts/person'
import { useAccess } from 'contexts/access'
import { useUserPermissions } from 'contexts/unit-permissions'
import { useUpgradable } from 'hooks/upgradable'
import { useWithinQuota } from 'hooks/within-quota'
import { getTypeModule, getReferenceDate, getPluralizedType } from 'pages/processes/utilities'
import { getDate } from 'utilities/date-time'
import { anyFieldsDisplayable } from 'utilities/access'
import { pick } from 'utilities/object'

export const usePersonProcess = ({ type }) => {
    const { person } = usePerson()

    const {
        check,
        checkModule
    } = useAccess()

    const { checkUnitPermission } = useUserPermissions()

    const module = getTypeModule(type)

    const manageAccess = check('users:manage') || (
        (['onboarding', 'offboarding'].includes(type) && checkUnitPermission('unit:employment-lifecycles:manage')) ||
        (type === 'process' && checkUnitPermission('unit:processes:manage'))
    )

    const available = checkModule(module)
    const upgradable = useUpgradable()({ module })

    const [withinQuota] = useWithinQuota({
        key: module,
        dependencies: [available, check('users:manage'), !upgradable]
    })

    const [state, setState] = useState(empty)

    useEffect(() => {
        if(available && manageAccess && type && !!person?.id) {
            const processData = { type, concerns: person }
            const date = getReferenceDate(processData)
            const startable = getStartable(processData)

            setState(state => ({
                ...((person.id === state.userId) ?
                    state :
                    empty
                ),
                date,
                startable
            }))

            const getProcess = async userId => {
                setState(state => ({
                    ...state,
                    fetching: true
                }))

                const { ok, response } = await get({
                    path: `/${getPluralizedType(type)}`,
                    params: { concernsId: userId }
                })

                if(ok) {
                    const commonState = {
                        fetching: false,
                        hasFetched: true,
                        stats: {},
                        id: null
                    }

                    if(!!response?.items?.length) {
                        const [process] = response.items

                        return void setState(state => ({
                            ...state,
                            ...commonState,
                            ...(!state.date ? { date: getReferenceDate(process) } : null),
                            startable: false,
                            stats: pick(process, 'completedTaskCount', 'overdueTaskCount', 'taskCount'),
                            id: process.id
                        }))
                    }

                    setState(state => ({
                        ...state,
                        ...commonState
                    }))
                }
            }

            getProcess(person.id)
        } else {
            setState(empty)
        }
    }, [available, manageAccess, person?.id, type])

    return {
        type,
        withinQuota,
        ...state
    }
}

export const useGetNavigationItems = (profile, lookingAtMyOwnProfile, items) => {
    const { formatMessage } = useIntl()

    return items
        .map(section => ({
            ...section,
            fields: section.fields.map(field => profile[field])
        }))
        .filter(({ id, fields, access }) => {
            if(access?.available === false) {
                return false
            }

            if(id === 'salary') {
                return anyFieldsDisplayable(fields, false)
            }

            if(id === 'roles') {
                if(profile.status?.active === false) {
                    return false
                }

                return true
            }

            return anyFieldsDisplayable(fields)
        })
        .map(({ id }) => ({
            id,
            text: formatMessage({
                id: (id === 'salary' && lookingAtMyOwnProfile)
                    ? 'salary_heading_yours'
                    : `person_pane_about_section_${id}`,
                defaultMessage: id
            })
        }))
}

const getStartable = process => {
    if(!process.concerns) {
        return false
    }

    const today = getDate()
    const lastDayOfWork = getReferenceDate({ ...process, type: 'offboarding' })

    // Consider not startable if the employee has quit
    if(!!lastDayOfWork && lastDayOfWork < today) {
        return false
    }

    const firstDayOfWork = getReferenceDate({ ...process, type: 'onboarding' })

    if(process.type === 'onboarding') {
        return !firstDayOfWork || (!!firstDayOfWork && today < firstDayOfWork)
    }

    if(process.type === 'offboarding') {
        return !!lastDayOfWork && today < lastDayOfWork
    }

    if(process.type === 'process') {
        return (!firstDayOfWork || today > firstDayOfWork) && (!lastDayOfWork || today < lastDayOfWork)
    }

    return false
}

const empty = {
    fetching: false,
    date: null,
    startable: false,
    stats: {},
    id: null,
    concernsId: null,
    hasFetched: false
}